import Logo from '../../../assets/logo-simple.svg?react';
import { Project } from '../../../types/graphqlTypes';
import classNames from 'classnames';
import { lazy, memo, Suspense, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import { Button } from '../../../components/Button';
import { RendererReadOnlyContext } from '../../../contexts/RendererReadOnlyContext';
import { T } from '../../../translation/src';
import { ExportModal, ExportType } from '../../project/ExportModal';
import { AccountSelect } from '../../user/AccountSelect';
import { HeaderModesBar, ModeConfig } from '../ModesBar';
import { Toolbar } from '../Toolbar';
import { WarningLabel } from '../../../components/WarningLabel';
import { useCustomSelector } from '../../../redux/store';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import { RendererContext } from '../../../contexts/RendererContext';
import { ExportOrthophotoModal } from '../../project/ExportOrthophotoModal';
import { UserContext } from '../../../contexts/UserContext';
import { PersonaButton } from '../../../components/PersonaButton';
import { PERSONAS } from '../../../config/personas';
import { usePersonaManager } from '../../../hooks/usePersonaManager';
import { Tooltip } from '../../../components/Tooltip';
import { LeftPanelTabs } from '../RendererLeftPanel';
import { ExportsDownloadButton } from './ExportsDownloadButton';
import { NotificationsDropdownButton } from './NotificationsDropdownButton';
import { useHideDebugButton } from '../../Properties/integration/UserSettingsIntegration';
import { ProjectShareModal } from '../../project/ProjectShareModal';
import { isShareMode } from '../../../share/shareMode';

// Sheets bring the print engine (pdf-lib, fontkit): loaded when opened.
const SheetsOverlay = lazy(() => import('../../sheets/SheetsOverlay'));

interface RendererHeaderProps {
  project: Pick<Project, 'id' | 'name' | 'description' | 'previewUrl' | 'settings'>;
  minimize?: boolean; // remove elements to make it readable on small devices/windows
  modes?: ModeConfig[];
  currentModeId?: string;
  onModeChange?: (modeId: string) => void;
  disabledModes?: Set<string>;
  leftPanelTab?: LeftPanelTabs;
}

const RendererHeader_: React.FC2<RendererHeaderProps> = ({ project, minimize, modes = [], currentModeId, onModeChange, disabledModes, leftPanelTab }) => {
  const { organisationId } = useParams();
  const isReadOnly = useContext(RendererReadOnlyContext);
  const { value: exportModalOpen, setFalse: closeExportModal, setTrue: openExportModal } = useBoolean();
  const { value: shareModalOpen, setFalse: closeShareModal, setTrue: openShareModal } = useBoolean();
  const { value: sheetsOpen, setFalse: closeSheets, setTrue: openSheets } = useBoolean();
  // Read-only viewers still switch personas to see each one's end result (no tools, no mode tabs).
  const visiblePersonas = isReadOnly ? PERSONAS.filter((persona) => !persona.hiddenInReadOnly) : PERSONAS;
  const { mdDevice } = useDeviceSize();
  const { performanceWarning } = useCustomSelector((state) => state.rendererProvider, ['performanceWarning']);
  const { viewer } = useContext(RendererContext);

  // Persona system integration
  const { activePersona, switchPersona } = usePersonaManager(viewer);

  // The export type is situational: the Layers tab exports point clouds,
  // anywhere else exports floors (each floor following its wall source).
  const exportType = leftPanelTab === LeftPanelTabs.LAYERS ? ExportType.POINT_CLOUDS : ExportType.FLOORS;

  const user = useContext(UserContext);
  // A share visitor without a session gets a way to log in; anyone with one keeps their account menu.
  const showLogin = isShareMode() && !user?.id;
  const { hideDebugButton } = useHideDebugButton();

  const onNavigateProject = useCallback(() => {
    /**
     * TODO: find better way to navigate and destroy everything
     * case: click on project with map view enabled, and click back on logo (https://pointorama.atlassian.net/jira/software/projects/PK/boards/2?selectedIssue=PK-7)
     */
    window.location.href = `${window.location.origin}/organisations/${organisationId}/projects`;
  }, [organisationId]);

  // A full page load, not a router navigation: it tears the viewer down and leaves the /share URL,
  // so the share header stops being sent before the login request goes out.
  const onNavigateLogin = useCallback(() => {
    // Carry the share URL along so the login page can come back here afterwards
    // (the login page reads this `redirect` parameter; a build without that support ignores it).
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(`/login?redirect=${encodeURIComponent(returnTo)}`);
  }, []);

  const toggleDebugOverlay = useCallback(() => {
    if (viewer?.debugOverlay) {
      viewer.debugOverlay.toggle();
    }
  }, [viewer]);

  return (
    <>
      <div data-panel="header" className={classNames('z-20 bg-[rgb(241,243,243)] dark:bg-[rgb(18,18,18)] dark:text-white', minimize ? 'px-4' : 'p-4')}>
        {/* Main header row */}
        <div className="flex items-center relative">
          {/* Logo and Persona buttons - left side */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <Logo className="flex-shrink-0 cursor-pointer h-7" onClick={onNavigateProject} />

            {/* Divider between logo and personas */}
            {!minimize && <div className="h-8 w-px bg-gray-300 dark:bg-gray-600" />}

            {/* Persona buttons next to logo */}
            {!minimize && (
              <div className="flex gap-1">
                {visiblePersonas.map((persona) => {
                  const isActive = activePersona?.name.toLowerCase() === persona.id;

                  return <PersonaButton key={persona.id} icon={persona.icon} name={persona.name} active={isActive} onClick={() => switchPersona(persona.id)} />;
                })}
              </div>
            )}
          </div>

          {/* Center area - Mode buttons - absolutely centered on screen */}
          {!minimize && !isReadOnly && (
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <HeaderModesBar modes={modes} currentModeId={currentModeId} onModeChange={onModeChange} disabledModes={disabledModes}>
                <Toolbar />
              </HeaderModesBar>
            </div>
          )}

          {/* Share button and account - right side */}
          <div className="flex items-center gap-4 flex-shrink-0 ml-auto">
            {user?.isSuperAdmin && !hideDebugButton && (
              <Tooltip message={<span>Debug Panel</span>} delay={0}>
                <Button variant="secondary" onClick={toggleDebugOverlay} className={minimize ? 'w-10 h-10' : ''}>
                  {!minimize && <span>Debug</span>}
                </Button>
              </Tooltip>
            )}
            {/* The only way to reach notifications here, so shown to anyone signed in. */}
            {!!user?.id && <NotificationsDropdownButton />}
            {/* Exporting is viewing in another format: available to every viewer, guests and share visitors included. */}
            {!minimize && <ExportsDownloadButton />}
            {!isReadOnly && !minimize && (
              <Button variant="secondary" onClick={openShareModal}>
                <span>
                  <T _str="share" swc />
                </span>
              </Button>
            )}
            {!minimize && (
              <Button variant="primary" onClick={openExportModal}>
                <span>
                  <T _str="export" swc />
                </span>
              </Button>
            )}
            {showLogin ? (
              <Button variant="secondary" onClick={onNavigateLogin}>
                <span>
                  <T _str="log in" swc />
                </span>
              </Button>
            ) : (
              <AccountSelect variant="header" forceReload={true} onlyIcon={minimize} size={mdDevice ? 'small' : 'large'} />
            )}
          </div>
        </div>
      </div>

      {/* Performance warning */}
      {performanceWarning ? <WarningLabel type="warning" label={<T _str="poor performance detected. Please ensure your browser is utilizing GPU" swc />} /> : null}

      {/* Modals */}
      {exportModalOpen && <ExportModal open={exportModalOpen} onClose={closeExportModal} exportType={exportType} onOpenSheets={isReadOnly ? undefined : openSheets} />}
      {shareModalOpen && <ProjectShareModal open={shareModalOpen} onClose={closeShareModal} project={project} />}
      {sheetsOpen && (
        <Suspense fallback={null}>
          <SheetsOverlay onClose={closeSheets} />
        </Suspense>
      )}
      <ExportOrthophotoModal project={project} />
    </>
  );
};

export const RendererHeader = memo(RendererHeader_);
